import * as Sentry from '@sentry/react'
import zentakeApi from 'api/index'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import map from 'lodash/map'
import toPairs from 'lodash/toPairs'
import type { Answer } from 'types/answer'
import type { ResponseWithAnswersToHiddenQuestions } from 'types/response'
import {
  getMatrixCoordinateId,
  getTableRow,
  hasText,
  isFilledArray,
} from 'utils/helpers'

type ChoiceDetails = {
  question_choice: string | null | undefined
  text_details: string | null | undefined
}

type Cell = {
  question_column: number
  question_row: number
}

type SaveAnswerBatch = {
  response: string
  question: number
  answer_raw: string | string[] | null | undefined
  choice_details?: ChoiceDetails[] | null | undefined
}

type MatrixAnswer = {
  answer_matrix_other_row?: string
  question_row?: string
  question_column: string
  text_value: string | null | undefined
  response: string
}

export type RepeatSectionResponse = {
  id: string
  question: number
  answer_raw: string
  order: number
}

export type RepeatSectionAnswer = {
  page_id: string
  original_page_id: string
  order: number
  answers: RepeatSectionResponse[]
}

type ResponseSaverData = {
  create_matrix_row: Array<{
    key: string
    response: string
    question: number
  }>
  delete_answer: Array<string>
  delete_answer_file: Array<{
    file: string | null | undefined
    question: number
    response: string
  }>
  delete_answer_matrix: Array<string>
  extraResponses: Array<string>
  mode: string
  response: ResponseWithAnswersToHiddenQuestions
  save_answer_batch: SaveAnswerBatch[]
  save_answer_matrix: MatrixAnswer[]
  save_answer_repeat_section: RepeatSectionAnswer[]
  submit: boolean | null | undefined
  updatedCellQuestions: Array<Cell>
  updatedQuestions: Array<number>
  lock_internal_form: boolean | null | undefined
}

type Payload = {
  response_id: string | null | undefined
  response_ids: string[] | null | undefined
  delete_answer_file: Array<{
    file: string | null | undefined
    question: number
    response: string
  }>
  delete_answer: Array<string>
  save_answer_batch: Array<SaveAnswerBatch>
  create_matrix_row: Array<{
    key: string
    response: string
    question: number
  }>
  delete_answer_matrix: string[]
  save_answer_matrix: MatrixAnswer[]
  save_answer_repeat_section: RepeatSectionAnswer[]
  submit: boolean
  lock_internal_form: boolean | null | undefined
}

function filterEmptyRepeatSections(repeatSections: RepeatSectionAnswer[]) {
  return repeatSections.filter((section) => section.answers.length)
}

function mapAnswerChoiceDetails(answer: Answer): ChoiceDetails[] | null {
  if (answer.text_details == null) return null

  if (hasText(answer.text_details)) {
    return [
      {
        question_choice: answer.answer_raw,
        text_details: answer.text_details,
      },
    ]
  }

  if (isObject(answer.text_details)) {
    return map(
      toPairs(answer.text_details),
      ([question_choice, text_details]) => ({
        question_choice,
        text_details: text_details as string,
      })
    )
  }
  return null
}

class SaveAnswersError extends Error {
  answerErrors: { [questionId: number]: Error }

  constructor(message: string, answerErrors: { [questionId: number]: Error }) {
    super(message)
    this.answerErrors = answerErrors
  }
}

class ResponseSaver {
  answerFilesToSubmit: Array<Promise<any>> = []

  data: ResponseSaverData = {
    create_matrix_row: [],
    delete_answer: [],
    delete_answer_file: [],
    delete_answer_matrix: [],
    extraResponses: [],
    mode: '',
    response: {
      answers: [],
      answersToHiddenQuestions: [],
      created_at: '',
      id: '',
      internal_user: null,
      internal_locked: false,
      is_archived: false,
      is_scheduled: false,
      is_submitted: false,
      name: '',
      uid: '',
    },
    save_answer_batch: [],
    save_answer_matrix: [],
    save_answer_repeat_section: [],
    submit: false,
    updatedCellQuestions: [],
    updatedQuestions: [],
    lock_internal_form: false,
  }

  questionError: { [questionId: number]: Error } = {}

  questionsForBatchUpdate: Array<{
    answer: string | null | undefined
    choice_details?: ChoiceDetails[] | null | undefined
    id: number
  }> = []

  constructor(data: ResponseSaverData) {
    this.data = data
  }

  _handleError(error: Error, questionId: number) {
    this.questionError[questionId] = error
  }

  markFileAnswerToDelete(questionId: number) {
    const { response } = this.data

    this.data.delete_answer_file.push({
      response: response.id,
      question: questionId,
      file: null,
    })
  }

  submitFileAnswer(answer: Answer, questionId: number) {
    const { mode, response } = this.data

    const filePromise = zentakeApi
      .saveAnswerFiles(response.id, questionId, answer.answer_files, mode)
      .catch((error: Error) => {
        Sentry.captureException(error, {
          extra: this.data,
        })
        this._handleError(error, questionId)
      })

    this.answerFilesToSubmit.push(filePromise)
  }

  saveFileAnswer(answer: Answer, questionId: number) {
    if (answer.answer_files == null || answer.answer_files.length === 0) {
      this.markFileAnswerToDelete(questionId)
      return
    }

    this.submitFileAnswer(answer, questionId)
  }

  saveGeneralAnswer(answer: Answer, questionId: number) {
    if (answer.answer_raw !== '') {
      const text_details = mapAnswerChoiceDetails(answer)

      this.questionsForBatchUpdate.push({
        id: questionId,
        answer: answer.answer_raw,
        ...(isFilledArray(text_details)
          ? { choice_details: text_details }
          : {}),
      })
      return
    }

    const hasIdAndNoText = answer.answer_raw === '' && hasText(answer.id)

    if (hasIdAndNoText) {
      this.data.delete_answer.push(answer.id || '')
    }
  }

  saveCellAnswers(
    cell: Cell,
    newMatrixRowsMap: Record<string, string>,
    rowNumber: number
  ) {
    const { response } = this.data
    const { question_row, question_column } = cell

    const rowId = getMatrixCoordinateId(question_row)
    const columnId = getMatrixCoordinateId(question_column)

    const answerCell = response.answers?.find(
      (answer) =>
        getMatrixCoordinateId(answer.question_column) === columnId &&
        getMatrixCoordinateId(getTableRow(answer)) === rowId
    )

    if (!answerCell) return

    const answerCellRow: string = getTableRow(answerCell)

    const needsNewRow =
      answerCell?.local === true && answerCell?.willRemove !== true

    let rowIdToUse = needsNewRow
      ? newMatrixRowsMap[answerCellRow]
      : answerCellRow

    if (needsNewRow && rowIdToUse == null) {
      rowIdToUse = `new_${response?.id}_${
        answerCell?.questionId || ''
      }_${rowNumber}`
      this.data.create_matrix_row.push({
        key: rowIdToUse,
        response: response?.id,
        question: answerCell?.questionId || 0,
      })
      newMatrixRowsMap[answerCellRow] = rowIdToUse
    }

    if (hasText(answerCell?.id) && answerCell?.willRemove === true) {
      this.data.delete_answer_matrix.push(answerCell.id || '')
    } else {
      const responseId = response.id
      const selectedRowId = needsNewRow ? rowIdToUse : rowId
      const text = answerCell?.text_value
      const useExtraRow = needsNewRow || typeof rowId === 'string'

      this.data?.save_answer_matrix?.push({
        ...(useExtraRow
          ? { answer_matrix_other_row: selectedRowId }
          : { question_row: selectedRowId }),
        question_column: columnId,
        text_value: text,
        response: responseId,
      })
    }
  }

  saveAnswer(questionId: number) {
    const answer = this.data.response.answers?.find(
      (a) => a.question === questionId
    )
    if (!answer) return

    if (answer.image || answer.text || answer.created_at || answer.updated_at) {
      return
    }

    if (answer.isFile) {
      this.saveFileAnswer(answer, questionId)
      return
    }

    if (!isNil(questionId) && !isNil(answer?.answer_raw)) {
      this.saveGeneralAnswer(answer, questionId)
    }
  }

  saveAnswers() {
    const { updatedQuestions } = this.data

    for (const questionId of updatedQuestions) {
      this.saveAnswer(questionId)
    }
  }

  createAnswersBatch() {
    if (!isFilledArray(this.questionsForBatchUpdate)) return

    const { response } = this.data

    this.data.save_answer_batch = this.questionsForBatchUpdate.map(
      ({ id, answer, choice_details }) => ({
        response: response.id,
        question: id,
        answer_raw: answer,
        ...(choice_details == null ? {} : { choice_details }),
      })
    )
  }

  saveMatrixCellAnswers() {
    const newMatrixRowsMap = {}

    const { updatedCellQuestions } = this.data

    updatedCellQuestions.forEach((cell, index) =>
      this.saveCellAnswers(cell, newMatrixRowsMap, index)
    )
  }

  deleteAnswersFromHiddenQuestions() {
    const { response } = this.data
    const { answersToHiddenQuestions } = response

    answersToHiddenQuestions.forEach((answer) => {
      if (answer.id) {
        this.data.delete_answer.push(answer.id)
      }
    })
  }

  async submit() {
    const {
      create_matrix_row,
      delete_answer,
      delete_answer_file,
      delete_answer_matrix,
      extraResponses,
      response,
      save_answer_batch,
      save_answer_matrix,
      save_answer_repeat_section,
      submit,
      lock_internal_form,
    } = this.data

    const payload: Payload = {
      create_matrix_row,
      delete_answer,
      delete_answer_file,
      delete_answer_matrix,
      response_id: extraResponses?.length ? null : response.id,
      response_ids: extraResponses?.length ? extraResponses : null,
      save_answer_batch,
      save_answer_matrix,
      save_answer_repeat_section: filterEmptyRepeatSections(
        save_answer_repeat_section
      ),
      submit: submit || false,
      lock_internal_form,
    }

    try {
      await zentakeApi.saveAllAnswers(payload, this.data.mode)
      this.data.create_matrix_row = []
      this.data.delete_answer = []
      this.data.delete_answer_file = []
      this.data.delete_answer_matrix = []
      this.data.save_answer_batch = []
      this.data.save_answer_matrix = []
      this.data.save_answer_repeat_section = []
      this.data.submit = false
      this.questionsForBatchUpdate = []
      this.data.updatedCellQuestions = []
      this.data.updatedQuestions = []
    } catch (error) {
      Sentry.captureException(error, {
        extra: this.data,
      })
      throw error
    }
  }

  throwErrors(): boolean | null | undefined {
    if (isEmpty(this.questionError)) {
      return true
    }

    const e = new SaveAnswersError('Fail to save answers', this.questionError)
    e.answerErrors = this.questionError

    Sentry.captureException(e, {
      extra: this.data,
    })
    throw e
  }

  async execute(): Promise<boolean> {
    this.saveAnswers()
    this.createAnswersBatch()
    this.saveMatrixCellAnswers()
    this.deleteAnswersFromHiddenQuestions()

    await Promise.all([...this.answerFilesToSubmit])

    this.throwErrors()

    await this.submit()

    return true
  }
}

export default ResponseSaver
